import { Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { SCROLL_INTO_VIEW_TO_TOP } from '../../../../constants/common'
import { useUserData } from '../../../../hooks'
import { isStaffUser } from '../../../../utils/user'
import Button, { BUTTON_HEIGHT_24, ButtonArrow } from '../../../Button'
import { FileUploadConstantInputWrap } from '../../../File'
import { getTableVisibleColumns, Table } from '../../../Table'
import { TaskTableRow } from '../../../Task'
import { useRequestProfileContext } from '../../RequestProfile.hooks'
import {
  QA_NAME_REQUEST_PROFILE_OPEN_ACTIVITIES_SHOW_MORE,
  QA_NAME_REQUEST_PROFILE_OPEN_ACTIVITIES_TABLE,
  REQUEST_PROFILE_OPEN_ACTIVITIES_PAGE_SIZE,
  REQUEST_PROFILE_OPEN_ACTIVITIES_TABLE_COLUMNS_CONFIG,
} from '../RequestProfileOpenActivities.constants'
import { useRequestProfileOpenActivitiesTableActions } from '../RequestProfileOpenActivities.hooks'
import {
  getRequestProfileOpenActivitiesFooterButtonText,
  getRequestProfileOpenActivitiesItemActions,
  getRequestProfileOpenActivitiesNextStepCount,
} from '../RequestProfileOpenActivities.utils'
import { RequestProfileOpenActivitiesSkeleton } from '../RequestProfileOpenActivitiesSkeleton'

import { renderRequestProfileOpenActivitiesCell } from './RequestProfileOpenActivitiesTable.utils'

import * as styles from './RequestProfileOpenActivitiesTable.module.css'

function RequestProfileOpenActivitiesTable({
  columns = getTableVisibleColumns(
    REQUEST_PROFILE_OPEN_ACTIVITIES_TABLE_COLUMNS_CONFIG
  ),
  openActivities,
  scrollContainerRef,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const containerRef = useRef(null)

  const currentUser = useUserData()
  const canEdit = isStaffUser(currentUser)

  const { handleReloadActivitiesTab } = useRequestProfileContext()

  const {
    handleReload,
    handleShowLess,
    handleShowMore,
    isAllShown,
    isLoading,
    isLoadingMore,
    total,
    visibleItems,
  } = openActivities

  async function handleReloadAfterAction() {
    onReloadCounters?.()

    handleReloadActivitiesTab?.()

    return handleReload()
  }

  const {
    getFileAttachInputProps,
    handleChangeStatus,
    handleClickEmailTag,
    handleClickFileTag,
    handleEditActivity,
    modals,
    setItemAction,
  } = useRequestProfileOpenActivitiesTableActions({
    onReload: handleReloadAfterAction,
  })

  function getActions(item) {
    return getRequestProfileOpenActivitiesItemActions(item, { canEdit })
  }

  function handleClickRow(item) {
    if (canEdit) {
      handleEditActivity(item)
    }
  }

  function handleShowLessClick() {
    handleShowLess()

    containerRef.current?.scrollIntoView(SCROLL_INTO_VIEW_TO_TOP)
  }

  const hasFooter = total > REQUEST_PROFILE_OPEN_ACTIVITIES_PAGE_SIZE

  const nextStepCount = getRequestProfileOpenActivitiesNextStepCount(
    total,
    visibleItems.length
  )

  return isLoading && visibleItems.length === 0 ? (
    <RequestProfileOpenActivitiesSkeleton />
  ) : (
    <div
      className={styles.RequestProfileOpenActivitiesTable}
      ref={containerRef}
    >
      <FileUploadConstantInputWrap
        getFileAttachInputProps={getFileAttachInputProps}
      >
        <Table
          TableRow={TaskTableRow}
          actionsColumnWidth={0}
          arrowNavigationConfig={{ disabled: true }}
          className={styles.table}
          columns={columns}
          containerRef={scrollContainerRef}
          context={QA_NAME_REQUEST_PROFILE_OPEN_ACTIVITIES_TABLE}
          headerRowProps={{
            className: styles.headerRow,
            resizable: false,
          }}
          items={visibleItems}
          rowProps={{
            Actions: null,
            disabled: true,
            getActions,
            hideCheckbox: true,
            renderCell: renderRequestProfileOpenActivitiesCell,
            onCallAction: setItemAction,
            onChangeStatus: handleChangeStatus,
            onClick: handleClickRow,
            onClickEmailTag: handleClickEmailTag,
            onClickFileTag: handleClickFileTag,
          }}
          tableRowClassName={styles.row}
        />
      </FileUploadConstantInputWrap>
      {hasFooter ? (
        <div className={styles.footer}>
          <ButtonArrow
            Button={Button.Text}
            height={BUTTON_HEIGHT_24}
            isLoading={isLoadingMore}
            isOpen={isAllShown}
            qaName={QA_NAME_REQUEST_PROFILE_OPEN_ACTIVITIES_SHOW_MORE}
            text={getRequestProfileOpenActivitiesFooterButtonText({
              isAllShown,
              isLoadingMore,
              nextStepCount,
              t,
            })}
            onClick={isAllShown ? handleShowLessClick : handleShowMore}
          />
        </div>
      ) : null}
      {modals.map(([id, element]) => (
        <Fragment key={id}>{element}</Fragment>
      ))}
    </div>
  )
}

export default RequestProfileOpenActivitiesTable
